/* eslint-disable no-console */

import React, { useState, useEffect } from 'react';

import { observer, inject } from 'mobx-react';
import ReactGA from 'react-ga';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import branch from 'branch-sdk';
import { hotjar } from 'react-hotjar';
import { HelmetProvider } from 'react-helmet-async';

import CauzeRouter from './CauzeRouter';
import ModalNotificationProvider from './views/ModalNotificationProvider/ModalNotificationProvider';
import CauzeSpinner from './components/CauzeSpinner/CauzeSpinner';
import mixpanel from 'mixpanel-browser';

import './styles';
import CauzeRoutingProvider from 'hooks/useCauzeRoutingContext';
import ElementsWrapper from 'components/ElementsWrapper';
import useSearchStore from 'stores/SearchStoreV2';
import useThemeStore from 'stores/ThemeStore';
import { MATCH_COMPANY_IDS } from 'util/constants';

const helmetContext = {};

const App = inject(
  'authStore',
  'profileStore',
  'uiStore',
  'userProfileStore',
)(
  observer(({ authStore, profileStore, uiStore, userProfileStore }) => {
    const [appReady, setAppReady] = useState(false);
    const searchStore = useSearchStore();
    const themeStore = useThemeStore();

    const initializeMixPanel = () => {
      if (global.MIXPANEL_PROJECT_TOKEN) {
        mixpanel.init(global.MIXPANEL_PROJECT_TOKEN, {
          debug: global.IS_LOCAL_OR_DEV,
        });
      } else {
        console.warn('Mixpanel key not provided.');
      }
    };

    const initializeReactGA = () => {
      ReactGA.initialize(global.GA_KEY, {
        // debug: global.IS_LOCAL_OR_DEV,
        titleCase: true,
      });
      ReactGA.pageview('/');
    };

    const initializeHotJar = () => {
      hotjar.initialize(global.HOTJAR_ID, global.HOTJAR_SNIPPET_VERSION);
    };

    const initializeBranch = async () => {
      try {
        branch.init(global.BRANCH_KEY, (error, _data) => {
          if (error) throw error;
          // uiStore.setHasApp(data.has_app);
        });
      } catch (err) {
        console.error(err);
      }
    };

    const handleAppInit = async () => {
      await authStore.onAppLoadFetch();
      await profileStore.getProfile();

      try {
        const companyMatch = await userProfileStore.getCompanyMatch({
          id: profileStore.data.id,
        });
        if (companyMatch.company.id === MATCH_COMPANY_IDS.ALTMAN_SOLON) {
          searchStore.setMatchCompanyId(companyMatch.company.id);
          themeStore.setTheme(themeStore.THEMES.ALTMAN_SOLON);
        }
      } catch (err) {
        console.error(err);
      }

      if (profileStore.data) {
        mixpanel.identify(profileStore.data.email.toLowerCase());
      }

      await uiStore.loadUiState();

      setAppReady(true);
    };

    useEffect(() => {
      initializeReactGA();
      initializeBranch();
      initializeHotJar();
      initializeMixPanel();
      handleAppInit();
    }, []);

    if (appReady && !authStore.isFetchingToken) {
      return (
        <HelmetProvider context={helmetContext}>
          <ElementsWrapper>
            <ModalNotificationProvider>
              <CauzeRoutingProvider uiStore={uiStore}>
                <CauzeRouter />
              </CauzeRoutingProvider>
            </ModalNotificationProvider>
          </ElementsWrapper>
        </HelmetProvider>
      );
    }
    return <CauzeSpinner fullscreen appSpinner />;
  }),
);

export default App;
